import { useScrollReveal } from "@/hooks/useScrollReveal";
import { lean } from "@/content";
import { Rich } from "@/lib/rich";

const plans = lean.pricing.plans;

export default function LeanPricing() {
  const { ref, revealed } = useScrollReveal();

  return (
    <section id="pricing" className="py-24 relative overflow-hidden bg-gradient-to-b from-white to-[#FAFAF8] dark:from-[#0C0C0E] dark:to-[#0A0A0A]">
      <div className="absolute top-[5%] right-[-5%] w-[400px] h-[400px] rounded-full bg-orange-100/30 blur-[130px] dark:bg-orange-500/10 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div
          ref={ref}
          className={`text-center mb-16 transition-all duration-700 ${
            revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-orange-50 border border-orange-200 text-orange-700 dark:bg-orange-500/[0.12] dark:border-orange-400/25 dark:text-orange-300 text-xs font-semibold tracking-widest uppercase mb-6">
            {lean.pricing.badge}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-stone-900 leading-tight mb-4 dark:text-white">
            <Rich text={lean.pricing.title} accentClassName="text-orange-600 dark:text-orange-500" />
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {plans.map((plan, i) => (
            <PlanCard key={plan.badge} plan={plan} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function PlanCard({
  plan,
  index,
}: {
  plan: {
    badge: string;
    title: string;
    situation: string;
    input: string;
    result: string;
    cta: string;
    accent: string;
  };
  index: number;
}) {
  const { ref, revealed } = useScrollReveal();
  const isOrange = plan.accent === "orange";

  return (
    <div
      ref={ref}
      className={`relative bg-white border rounded-3xl p-8 md:p-10 dark:bg-white/[0.04] transition-all duration-700 ${
        isOrange
          ? "border-orange-200 dark:border-orange-400/25 shadow-[0_8px_40px_rgba(234,88,12,0.08)] dark:shadow-[0_8px_40px_rgba(249,115,22,0.06)]"
          : "border-stone-200 dark:border-white/[0.08] shadow-[0_4px_24px_rgba(0,0,0,0.03)] dark:shadow-[0_4px_24px_rgba(0,0,0,0.2)]"
      } ${revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
      style={{ transitionDelay: `${index * 150}ms` }}
    >
      <span
        className={`inline-block px-3 py-1 rounded-full text-xs font-bold tracking-wide uppercase mb-4 ${
          isOrange
            ? "bg-orange-50 dark:bg-orange-500/[0.12] text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-400/25"
            : "bg-stone-100 dark:bg-white/[0.06] text-stone-600 dark:text-white/60 border border-stone-200 dark:border-white/[0.10]"
        }`}
      >
        {plan.badge}
      </span>
      <h3 className="text-xl md:text-2xl font-extrabold text-stone-900 dark:text-white mb-6">
        {plan.title}
      </h3>

      <div className="space-y-4 mb-8">
        <div>
          <span className="text-stone-600 text-xs font-semibold uppercase tracking-wider dark:text-white/60">{lean.pricing.labels.situation}</span>
          <p className="text-stone-700 text-sm leading-relaxed mt-1 dark:text-white/70">{plan.situation}</p>
        </div>
        <div>
          <span className="text-stone-600 text-xs font-semibold uppercase tracking-wider dark:text-white/60">{lean.pricing.labels.input}</span>
          <p className="text-stone-700 text-sm leading-relaxed mt-1 dark:text-white/70">{plan.input}</p>
        </div>
        <div>
          <span className="text-stone-600 text-xs font-semibold uppercase tracking-wider dark:text-white/60">{lean.pricing.labels.result}</span>
          <p className="text-stone-700 text-sm leading-relaxed mt-1 dark:text-white/70">{plan.result}</p>
        </div>
      </div>

      <a
        href="#contact"
        className={`block text-center w-full py-4 rounded-2xl font-bold text-sm transition-colors cursor-pointer whitespace-nowrap ${
          isOrange
            ? "bg-orange-600 text-white hover:bg-orange-700 dark:bg-orange-500 dark:text-[#0A0A0A] dark:hover:bg-orange-400"
            : "bg-stone-900 text-white hover:bg-stone-800 dark:bg-orange-500 dark:text-[#0A0A0A] dark:hover:bg-orange-400"
        }`}
      >
        {plan.cta}
      </a>
    </div>
  );
}